import { useEffect, useMemo, useRef, useState } from "react";
import { Check, X, Trophy, RotateCcw } from "lucide-react";
import { mediaUrl } from "@/lib/api";

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

const mask = (text, word) => {
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = [word, ...word.split(/\s+/).filter((w) => w.length > 3)].map(esc);
  return text.replace(new RegExp(`(${parts.join("|")})\\w*`, "gi"), "_____");
};

const buildQuestions = (qTerms, all) =>
  shuffle(qTerms).slice(0, 10).map((t) => ({
    term: t,
    options: shuffle([t, ...shuffle(all.filter((o) => o.id !== t.id)).slice(0, 3)]),
  }));

export const MultipleChoiceQuiz = ({ terms, allTerms, variant = "definizioni", onFinish }) => {
  const [seed, setSeed] = useState(0);
  const questions = useMemo(() => buildQuestions(terms, allTerms || terms), [terms, allTerms, seed]); // eslint-disable-line react-hooks/exhaustive-deps
  const [n, setN] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [ratio, setRatio] = useState(null);
  const sent = useRef(false);

  useEffect(() => {
    if (n >= questions.length && questions.length && !sent.current) {
      sent.current = true;
      onFinish?.(score, questions.length);
    }
  }, [n]); // eslint-disable-line react-hooks/exhaustive-deps

  const restart = () => { sent.current = false; setSeed((s) => s + 1); setN(0); setPicked(null); setScore(0); };

  if (n >= questions.length)
    return (
      <div className="ink-box mx-auto max-w-xl rounded-3xl bg-white p-10 text-center" data-testid="quiz-result">
        <Trophy className="mx-auto h-12 w-12 text-[var(--sun)]" strokeWidth={2.5} />
        <p className="font-display mt-3 text-4xl font-bold" data-testid="quiz-score">{score} / {questions.length}</p>
        <p className="mt-2 font-semibold text-neutral-600">{score === questions.length ? "Perfetto! Sei un campione!" : score >= questions.length / 2 ? "Bravo! Ancora un piccolo sforzo." : "Ripassa le flashcard e riprova!"}</p>
        <button onClick={restart} data-testid="quiz-restart" className="ink-box-sm ink-press mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--sun)] px-5 py-2 font-extrabold"><RotateCcw className="h-4 w-4" /> Rifai il quiz</button>
      </div>
    );

  const q = questions[n];
  const choose = (o) => {
    if (picked) return;
    setPicked(o);
    if (o.id === q.term.id) setScore((s) => s + 1);
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="quiz">
      <div className="mb-3 flex justify-between text-sm font-extrabold">
        <span data-testid="quiz-progress">Domanda {n + 1} di {questions.length}</span>
        <span>Punti: {score}</span>
      </div>
      {variant === "fumetti" ? (
        <div className="ink-box mb-8 overflow-hidden rounded-3xl bg-white">
          <p className="px-6 pt-4 text-xs font-extrabold uppercase tracking-widest text-neutral-500">Quale parola racconta questo fumetto?</p>
          <div className="mt-3 overflow-hidden border-t-2 border-[var(--ink)]" style={{ aspectRatio: ratio ? `${ratio * 2}` : "2" }}>
            <img key={q.term.id} src={mediaUrl(q.term.comic_path)} alt="Fumetto misterioso" onLoad={(e) => setRatio(e.target.naturalWidth / e.target.naturalHeight)} className="w-full" data-testid="quiz-comic-image" />
          </div>
        </div>
      ) : (
        <div className="bubble mb-10 px-6 py-5">
          <p className="text-xs font-extrabold uppercase tracking-widest text-neutral-500">Quale parola corrisponde a questa definizione?</p>
          <p className="mt-2 text-lg font-bold leading-relaxed" data-testid="quiz-definition">{mask(q.term.definition, q.term.word)}</p>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {q.options.map((o, i) => {
          const state = !picked ? "bg-white hover:bg-[var(--paper)]" : o.id === q.term.id ? "bg-emerald-300" : o.id === picked.id ? "bg-red-300" : "bg-white opacity-60";
          return (
            <button key={o.id} onClick={() => choose(o)} data-testid={`quiz-option-${i}`} className={`ink-box-sm font-display flex items-center justify-between rounded-2xl px-5 py-4 text-left text-lg font-bold transition-colors ${state}`}>
              {o.word}
              {picked && o.id === q.term.id && <Check className="h-5 w-5" />}
              {picked && o.id === picked.id && o.id !== q.term.id && <X className="h-5 w-5" />}
            </button>
          );
        })}
      </div>
      {picked && (
        <button onClick={() => { setN(n + 1); setPicked(null); }} data-testid="quiz-next" className="ink-box-sm ink-press mt-6 w-full rounded-full bg-[var(--sky)] py-3 font-extrabold text-white">
          {n + 1 === questions.length ? "Vedi il risultato" : "Prossima domanda"}
        </button>
      )}
    </div>
  );
};
